import type { JSX, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Carregando } from '../componentes/Estados.js';
import { useAuth, type Papel } from './AuthContext.js';

/**
 * Onde mora a tela de troca de senha.
 *
 * Constante, e não literal espalhado, porque **três** lugares dependem de ela
 * ser a mesma: a rota em `App.tsx`, o link do `Layout` e a exceção do gate logo
 * abaixo. Se a do gate divergisse da rota, o redirecionamento entraria em laço
 * — a pessoa seria mandada para uma tela que a manda de volta, para sempre.
 */
export const CAMINHO_TROCA_SENHA = '/trocar-senha';

/**
 * Normaliza "um papel ou vários" para lista — Story 2.3.
 *
 * Fica exportado porque o **menu** precisa da mesma resposta que o porteiro: um
 * item que aparece e leva a `/sem-permissao` é pior do que item nenhum. Duas
 * cópias da regra divergiriam no primeiro papel novo, e o sintoma seria um menu
 * que mente — o tipo de defeito que ninguém reporta, só evita.
 */
export function papeisAceitos(papel: Papel | readonly Papel[]): readonly Papel[] {
  return typeof papel === 'string' ? [papel] : papel;
}

/**
 * Porteiro de rota — Story 8.1 (RF-03/04), com o gate de primeiro acesso da 1.6.
 *
 * Enquanto a sessão está sendo restaurada, mostra carregando: redirecionar
 * antes disso jogaria para o login quem tem sessão válida, a cada F5.
 *
 * Sem o papel exigido, **não renderiza o conteúdo nem por um frame** — o
 * `Navigate` acontece no lugar do filho, não depois dele.
 */
export function RotaProtegida({
  children,
  papel,
}: {
  children: ReactNode;
  /**
   * Omitido: basta estar autenticado.
   *
   * Aceita **um papel ou uma lista** desde a Story 2.3. O dashboard é de
   * `admin` **e** `gestor`, e até então não havia como dizer isso: a checagem
   * era `usuario.role !== papel`, igualdade estrita, então `papel="admin"`
   * barraria o gestor.
   *
   * A alternativa era um segundo componente para "um destes papéis". Ficaria
   * pior: o gate de primeiro acesso mora aqui dentro (logo abaixo), e duplicá-lo
   * é justamente onde nasce o laço de `Navigate` que trava a aba sem erro no
   * console. Um porteiro só, que sabe contar até dois.
   */
  papel?: Papel | readonly Papel[];
}): JSX.Element {
  const { usuario, restaurando } = useAuth();
  const local = useLocation();

  if (restaurando) return <Carregando rotulo="Verificando sua sessão…" />;

  if (usuario === undefined) {
    // `state.de` devolve o usuário ao destino original depois do login.
    return <Navigate to="/login" replace state={{ de: local.pathname + local.search }} />;
  }

  /*
   * Primeiro acesso — Story 1.6.
   *
   * A senha foi definida por quem criou o acesso, então mais de uma pessoa a
   * conhece. Antes de liberar qualquer tela, a pessoa passa pela troca.
   *
   * O gate vem ANTES da checagem de papel de propósito: um admin com senha
   * pendente que abrisse `/importacoes` seria mandado para a troca, e não para
   * "sem permissão" — a pendência é o motivo mais próximo.
   *
   * A exceção é a própria tela de troca. Sem ela, o redirecionamento se
   * alimentaria: `/trocar-senha` → `/trocar-senha` → … A checagem é por
   * caminho, e não por uma prop que a rota precisasse passar, porque esquecer
   * a prop produziria exatamente esse laço — e um laço de `Navigate` trava a
   * aba, sem erro no console que diga o porquê.
   */
  if (usuario.precisaTrocarSenha && local.pathname !== CAMINHO_TROCA_SENHA) {
    return <Navigate to={CAMINHO_TROCA_SENHA} replace />;
  }

  if (papel !== undefined && !papeisAceitos(papel).includes(usuario.role)) {
    return <Navigate to="/sem-permissao" replace />;
  }

  return <>{children}</>;
}
